import AppContext from "PFApp/app_context";
import { Button } from "PFComponents/button";
import AutoSelect from "PFComponents/select/autoselect";
import { TextArea } from "PFComponents/text/text_area";
import { useBookingApprovalRequest } from "PFCore/hooks/queries/bookings/approvals/use_booking_approval_request";
import { BookingApproval } from "PFTypes";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./modals.module.scss";
import ApprovalModal from "./parts/approval_modal";
import ApprovalTable from "./parts/approval_table";

type IOption = {
  id: number;
  text: string;
};
type IProps = {
  data: BookingApproval | BookingApproval[];
  handleClose: () => void;
  onSuccess?: (items?: BookingApproval[]) => void;
};

const Content = ({ data, handleClose, onSuccess }: IProps) => {
  const {
    store: {
      pages: {
        booking: {
          approvals: {
            data: { meta }
          }
        }
      }
    }
  } = useContext(AppContext);

  const arrifiedData = [data].flat();
  const { t } = useTranslation(["bookingModule", "translation"]);
  const { requestApproval } = useBookingApprovalRequest(...arrifiedData.map(({ id }) => id));

  const requesterIdFilter = meta.filters.fields?.requester_id;
  const [from, setFrom] = useState<{ id: number; text: string } | null>(null);
  const [note, setNote] = useState<string>();

  const fromOptions: IOption[] = requesterIdFilter.options.map(({ id, text }) => ({
    id,
    text
  }));

  const handleSubmit = () => {
    if (from === null) {
      return;
    }
    const payload = { assignee_id: from.id, request_reason: note };
    requestApproval(payload).then(() => {
      handleClose();
      onSuccess && onSuccess(arrifiedData);
    });
  };

  return (
    <>
      <div className={css.modal}>
        <h3 className={css.title}>{t("bookingModule:approvals.requestApproval")}</h3>
        <ApprovalTable data={data} />

        <div className={css.inputWrapper}>
          <AutoSelect
            values={from ? [from.text] : null}
            label={t("bookingModule:approvals.from")}
            displayValues={from?.text}
            closeOnChange
            multi={false}
            query={(term) =>
              Promise.resolve(fromOptions.filter(({ text }) => text.toLowerCase().includes(term)))
            }
            handleChange={(profiles) => {
              setFrom(profiles[0]);
            }}
          />
        </div>
        <div className={css.inputWrapper}>
          <div className={css.label}>{t("bookingModule:approvals.note")}</div>
          <TextArea
            className={css.textArea}
            value={note}
            onChange={setNote}
            maxLength={280}
            maxHeight={200}
          />
        </div>
        <hr className={css.separator} />
        <div className={css.buttons}>
          <Button text={t("translation:cancel")} kind="secondary" onClick={handleClose} />
          <Button
            text={t("bookingModule:approvals.requestApproval")}
            kind="primary"
            disabled={from === null || note === null}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </>
  );
};

const RequestApprovalModal = ({ handleClose, ...props }: IProps) => (
  <ApprovalModal qaId="bookingRequestApprovalModal" handleClose={handleClose}>
    <Content {...props} handleClose={handleClose} />
  </ApprovalModal>
);

export default RequestApprovalModal;
